import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Search, Filter, Phone, Mail, Calendar, CheckCircle, Clock,
    RefreshCw, MessageSquare, Trash2, ExternalLink
} from 'lucide-react';
import { contactLeadAPI } from '../../api/apiService';
import { toast } from 'react-hot-toast';

const parseLeadMessage = (msg) => {
    if (!msg) return { isStructured: false, company: '', address: '', message: '' };
    if (msg.includes('Company:') && msg.includes('Address:') && msg.includes('Message:')) {
        try {
            const companyPart = msg.split('Address:')[0].replace('Company:', '').trim();
            const addressPart = msg.split('Address:')[1].split('Message:')[0].trim();
            const messagePart = msg.split('Message:')[1].trim();
            return {
                isStructured: true,
                company: companyPart,
                address: addressPart,
                message: messagePart
            };
        } catch (e) {
            return { isStructured: false, company: '', address: '', message: msg };
        }
    }
    return { isStructured: false, company: '', address: '', message: msg };
};

const ContactLeads = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [selectedLead, setSelectedLead] = useState(null);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [deleteLeadId, setDeleteLeadId] = useState(null);

    useEffect(() => {
        fetchLeads();
    }, []);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            const response = await contactLeadAPI.getAll();
            setLeads(response.data);
        } catch (error) {
            console.error("Error fetching contact leads:", error);
            toast.error("Failed to load contact leads");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (status) => {
        if (!selectedLead) return;

        try {
            await contactLeadAPI.updateStatus(selectedLead.id, { status });
            toast.success(`Lead marked as ${status}`);
            fetchLeads();
            setIsStatusModalOpen(false);
            setSelectedLead(null);
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("Failed to update status");
        }
    };

    const handleDeleteLead = async (id) => {
        try {
            await contactLeadAPI.delete(id);
            toast.success("Enquiry deleted successfully");
            fetchLeads();
        } catch (error) {
            console.error("Error deleting enquiry:", error);
            toast.error("Failed to delete enquiry");
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'NEW': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30';
            case 'RESPONDED': return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30';
            case 'CLOSED': return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700/30';
            default: return 'bg-slate-500/10 text-slate-600 dark:text-slate-400';
        }
    };

    const filteredLeads = leads.filter(lead => {
        const matchesSearch =
            lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            lead.phone.includes(searchTerm) ||
            lead.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (lead.business_name && lead.business_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (lead.business_type && lead.business_type.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (lead.lead_type && lead.lead_type.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesStatus = statusFilter === 'ALL' || lead.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    return (
        <div className="p-6 bg-gray-50 dark:bg-slate-900 min-h-screen text-slate-900 dark:text-slate-100 transition-colors">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Contact Us Leads</h1>
                    <p className="text-slate-500 dark:text-slate-400">Manage enquiries from the public contact form</p>
                </div>
                <button
                    onClick={fetchLeads}
                    className="p-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition"
                >
                    <RefreshCw size={20} className={`text-gray-600 dark:text-slate-400 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 rounded-full -mr-8 -mt-8 px-4" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-500/10 rounded-2xl">
                            <MessageSquare className="text-blue-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Total Enquiries</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.length}</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full -mr-8 -mt-8" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-amber-500/10 rounded-2xl">
                            <Clock className="text-amber-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">New Enquiries</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.filter(l => l.status === 'NEW').length}</h3>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden relative group">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full -mr-8 -mt-8" />
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-emerald-500/10 rounded-2xl">
                            <CheckCircle className="text-emerald-600" size={24} />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Responded</p>
                            <h3 className="text-3xl font-black text-slate-900 dark:text-white leading-none mt-1">{leads.filter(l => l.status === 'RESPONDED').length}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700/50 mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center transition-all">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search by name, email, or subject..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-12 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all text-sm"
                    />
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <Filter size={18} className="text-slate-400" />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="flex-1 sm:flex-none px-4 py-2.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white focus:ring-2 focus:ring-slate-900 dark:focus:ring-white outline-none transition-all text-sm appearance-none cursor-pointer pr-10"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m19 9-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1.25rem' }}
                    >
                        <option value="ALL">All Status</option>
                        <option value="NEW">New</option>
                        <option value="RESPONDED">Responded</option>
                        <option value="CLOSED">Closed</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700/50 overflow-hidden transition-all">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                            <tr>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Contact</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Source</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Message Details</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Date</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Status</th>
                                <th className="p-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-right">Actions</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-slate-500 dark:text-slate-400 italic">Loading leads...</td>
                                </tr>
                            ) : filteredLeads.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-slate-500 dark:text-slate-400 italic">No enquiries found.</td>
                                </tr>
                            ) : (
                                filteredLeads.map((lead) => {
                                    const parsed = parseLeadMessage(lead.message);
                                    return (
                                        <tr key={lead.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-all border-l-4 border-transparent hover:border-emerald-500">
                                            <td className="p-5 align-top">
                                                <div className="font-bold text-slate-900 dark:text-white mb-0.5 whitespace-nowrap">{lead.name}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5 mt-1 whitespace-nowrap">
                                                    <Mail size={12} className="shrink-0" /> {lead.email}
                                                </div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5 mt-1 whitespace-nowrap">
                                                    <Phone size={12} className="shrink-0" /> {lead.phone}
                                                </div>
                                            </td>
                                            <td className="p-5 align-top">
                                                <span className={`inline-block px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${
                                                    lead.lead_type === 'Billing Enquiry'
                                                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                                                        : 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30'
                                                }`}>
                                                    {lead.lead_type || 'General Inquiry'}
                                                </span>
                                            </td>
                                            <td className="p-5 align-top max-w-md">
                                                <div className="font-bold text-slate-900 dark:text-white text-sm mb-1.5 uppercase tracking-tight">{lead.subject}</div>
                                                {lead.business_name && (
                                                    <div className="text-xs font-extrabold text-slate-700 dark:text-slate-200 mb-1 flex items-center gap-1.5">
                                                        <span>🏢 {lead.business_name}</span>
                                                        {lead.business_type && (
                                                            <span className="text-[10px] bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-500 dark:text-slate-400 font-medium">
                                                                {lead.business_type}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {parsed.isStructured ? (
                                                    <div className="space-y-1 mt-1.5">
                                                        {parsed.address && (
                                                            <div className="text-xs text-slate-500 dark:text-slate-400 flex items-start gap-1">
                                                                <span className="font-extrabold text-slate-700 dark:text-slate-300">Address:</span>
                                                                <span className="italic line-clamp-1">{parsed.address}</span>
                                                            </div>
                                                        )}
                                                        <div className="text-xs text-slate-600 dark:text-slate-300 font-medium line-clamp-2 bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-xl border border-slate-200/60 dark:border-slate-700 mt-1.5 italic">
                                                            "{parsed.message}"
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-xs text-slate-600 dark:text-slate-300 font-medium line-clamp-2 italic mt-1.5 bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-xl border border-slate-200/60 dark:border-slate-700">
                                                        "{lead.message}"
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-5 align-top text-sm whitespace-nowrap">
                                                <div className="text-slate-900 dark:text-white font-bold">{new Date(lead.created_at).toLocaleDateString()}</div>
                                                <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium uppercase mt-0.5">{new Date(lead.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                            <td className="p-5 align-top whitespace-nowrap">
                                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider text-center ${getStatusColor(lead.status)}`}>
                                                    {lead.status}
                                                </span>
                                            </td>
                                            <td className="p-5 align-top text-right whitespace-nowrap">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedLead(lead);
                                                            setIsStatusModalOpen(true);
                                                        }}
                                                        className="px-4 py-1.5 bg-slate-100 dark:bg-slate-700 hover:bg-slate-900 dark:hover:bg-white text-slate-600 dark:text-slate-300 hover:text-white dark:hover:text-slate-900 text-[10px] font-black uppercase tracking-[0.1em] rounded-lg transition-all"
                                                    >
                                                        Manage
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteLeadId(lead.id)}
                                                        className="text-slate-400 hover:text-red-500 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Status Modal */}
            {isStatusModalOpen && selectedLead && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl max-w-lg w-full p-8 border border-slate-200 dark:border-slate-700"
                    >
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 text-center">Manage Enquiry</h2>
                        
                        <div className="space-y-6 mb-8">
                            <div className="p-5 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-100 dark:border-slate-700">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <p className="font-bold text-slate-900 dark:text-white text-xl">{selectedLead.name}</p>
                                        <p className="text-sm text-emerald-600 font-bold">{selectedLead.email}</p>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${getStatusColor(selectedLead.status)}`}>
                                        {selectedLead.status}
                                    </span>
                                </div>
                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Subject</label>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white">{selectedLead.subject}</p>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Source / Lead Type</label>
                                            <p className="text-sm font-bold text-slate-900 dark:text-white">{selectedLead.lead_type || 'General Inquiry'}</p>
                                        </div>
                                    </div>
                                    {selectedLead.business_name && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Business Name</label>
                                                <p className="text-sm font-bold text-slate-900 dark:text-white">{selectedLead.business_name}</p>
                                            </div>
                                            {selectedLead.business_type && (
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Business Type</label>
                                                    <p className="text-sm font-bold text-slate-900 dark:text-white">{selectedLead.business_type}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div>
                                        {(() => {
                                            const parsed = parseLeadMessage(selectedLead.message);
                                            return parsed.isStructured ? (
                                                <div className="space-y-4">
                                                    {parsed.address && (
                                                        <div>
                                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Address</label>
                                                            <div className="mt-1 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 flex items-center gap-2">
                                                                <span>📍</span> {parsed.address}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Message</label>
                                                        <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 leading-relaxed italic whitespace-pre-line">
                                                            "{parsed.message}"
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Message</label>
                                                    <div className="mt-1 text-xs text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 leading-relaxed italic whitespace-pre-line">
                                                        "{selectedLead.message}"
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Update Status</label>
                                <div className="grid grid-cols-1 gap-2">
                                    {['NEW', 'RESPONDED', 'CLOSED'].map((status) => (
                                        <button
                                            key={status}
                                            onClick={() => handleStatusUpdate(status)}
                                            className={`w-full p-4 rounded-xl text-left text-[10px] font-black uppercase tracking-[0.15em] transition-all flex justify-between items-center ${selectedLead.status === status
                                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xl'
                                                : 'bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400'
                                                }`}
                                        >
                                            {status}
                                            {selectedLead.status === status && <CheckCircle size={14} />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => {
                                setIsStatusModalOpen(false);
                                setSelectedLead(null);
                            }}
                            className="w-full py-3 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
                        >
                            Close
                        </button>
                    </motion.div>
                </div>
            )}

            {/* Custom styled confirmation box modal overlay */}
            {deleteLeadId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-slate-800 rounded-3xl p-6 max-w-sm w-full border border-slate-200 dark:border-slate-700 shadow-2xl space-y-6"
                    >
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-red-100 dark:bg-red-500/10 rounded-2xl text-red-600 dark:text-red-400 shrink-0">
                                <Trash2 size={24} />
                            </div>
                            <div className="space-y-1.5">
                                <h3 className="text-base font-black text-slate-900 dark:text-white">Delete Enquiry</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed font-semibold">
                                    Are you sure you want to delete this enquiry? This action cannot be undone.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2.5 justify-end">
                            <button
                                onClick={() => setDeleteLeadId(null)}
                                className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-650 text-slate-700 dark:text-slate-200 font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all active:scale-95"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    handleDeleteLead(deleteLeadId);
                                    setDeleteLeadId(null);
                                }}
                                className="px-5 py-2.5 bg-red-650 hover:bg-red-700 text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all active:scale-95 shadow-lg shadow-red-600/15"
                            >
                                Yes, Delete
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default ContactLeads;
